import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { PxWindow } from '../components/PixelUI';
import { getCoin } from '../api';
import { fmtUsd, fmtBig, fmtCount } from '../catalog';
import { PctCell } from './Wire';

const NoSignal = () => <span className="nv-unread">no signal</span>;

const trustClass = (trust) => {
  if (trust === 'green') return 'buy';
  if (trust === 'yellow') return 'dim';
  return 'sell';
};

const CoinLoading = () => (
  <main className="nv-stars" style={{ minHeight: '70vh' }}>
    <section className="nv-section on-night" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
      <p className="font-crt" style={{ color: 'var(--nv-gold)', fontSize: 24 }}>tuning the wire…<span className="nv-cursor" /></p>
    </section>
  </main>
);

const CoinNotCarried = () => (
  <main className="nv-stars" style={{ minHeight: '70vh' }}>
    <section className="nv-section on-night" style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
      <p className="font-pixel" style={{ color: 'var(--nv-red)', fontSize: 13 }}>THE WIRE IS SILENT ON THAT COIN</p>
      <Link to="/wire" className="px-btn sm" style={{ marginTop: 24 }}><ArrowLeft size={13} /> Back to the wire</Link>
    </section>
  </main>
);

const MarketsWindow = ({ detail, tickers }) => (
  <PxWindow title={`${detail.symbol}.MARKETS`} tape className="nv-tape-term nv-scan">
    <div className="nv-term-lines" data-testid="markets-feed" style={{ maxHeight: 440, overflowY: 'auto' }}>
      {tickers.length === 0 && <div className="dim">the wire carried no markets for this coin</div>}
      {tickers.map((t, i) => (
        <div key={`${t.exchange}-${t.pair}-${i}`}>
          <span className={trustClass(t.trust)}>■</span>{' '}
          <span style={{ color: 'var(--nv-gold)' }}>{t.exchange ?? 'no signal'}</span>{' '}
          {t.pair}{' '}
          <span className="dim">last</span> {fmtUsd(t.price) ?? 'no signal'}{' '}
          <span className="dim">vol</span> {fmtBig(t.volume) ?? 'no signal'}
        </div>
      ))}
    </div>
    <p style={{ padding: '10px 18px', fontSize: 11, color: 'rgba(247,240,225,.45)' }}>
      ■ trust score exactly as the aggregator sent it · green is good
    </p>
  </PxWindow>
);

const StatsWindow = ({ detail }) => (
  <PxWindow title="GAUGE.PANEL" tape>
    <div style={{ padding: 18 }}>
      <div className="nv-kv"><span className="k">Mcap rank</span><span className="v">{detail.rank ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">Market cap</span><span className="v">{fmtBig(detail.market_cap) ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">Volume 24h</span><span className="v">{fmtBig(detail.volume24h) ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">High 24h</span><span className="v">{fmtUsd(detail.high24h) ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">Low 24h</span><span className="v">{fmtUsd(detail.low24h) ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">Circulating</span><span className="v">{fmtCount(detail.supply) ?? <NoSignal />}</span></div>
      <div className="nv-kv"><span className="k">Holders</span><span className="v"><NoSignal /></span></div>
    </div>
  </PxWindow>
);

const CoinTerminal = () => {
  const { coinId } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    setData(undefined);
    getCoin(coinId).then(setData);
  }, [coinId]);

  if (data === undefined) return <CoinLoading />;
  if (data === null) return <CoinNotCarried />;

  const { detail, tickers } = data;

  return (
    <main className="nv-stars" style={{ minHeight: '80vh' }}>
      <section className="nv-section on-night" style={{ paddingTop: 40, position: 'relative', zIndex: 1 }}>
        <div className="nv-section-inner">
          <Link to="/wire" style={{ color: 'var(--nv-tan)', fontSize: 12, letterSpacing: 2, textTransform: 'uppercase', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 22 }} data-testid="back-to-wire">
            <ArrowLeft size={14} /> back to the wire
          </Link>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 18, marginBottom: 28 }}>
            <h1 className="font-pixel" style={{ fontSize: 'clamp(22px, 4vw, 36px)', color: 'var(--nv-gold)', margin: 0 }} data-testid="coin-title">{detail.symbol}</h1>
            <span style={{ color: 'var(--nv-tan)' }}>{detail.name}</span>
            <span className="font-pixel" style={{ fontSize: 16, color: 'var(--nv-cream)' }}>{fmtUsd(detail.price) ?? 'no signal'}</span>
            <PctCell value={detail.change24h} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 26 }}>
            <MarketsWindow detail={detail} tickers={tickers} />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 26 }}>
              <StatsWindow detail={detail} />
              <PxWindow title="READ.ME" tape>
                <div style={{ padding: 18, fontSize: 12.5, lineHeight: 1.8 }}>
                  · every gauge above repeats the aggregator word for word, on a two-minute cache ·
                  holders is a figure this feed never sends, so it reads no signal — never zero
                </div>
              </PxWindow>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default CoinTerminal;
